import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext.js";
import { api, ApiError } from "../../api/client.js";
import type { PersonaSummary, SourceVideoSummary } from "../../api/types.js";

/**
 * Canonical Persona Swap Gate 2 workflow (2026-09): the user never picks a
 * WaveSpeed model id anymore — exactly two independent choices, Content
 * Mode and Video Length, are captured here at Gate 1 submission time and
 * sent to the backend, which resolves the actual Gate 2 model via its own
 * canonical matrix (`resolveGate2Model`, `packages/application/src/wavespeed/resolve-gate-two-model.ts`).
 * This mirrors that resolver's own type union exactly.
 */
type ContentMode = "SFW" | "SPICY" | "NSFW";
type VideoLength = "UP_TO_15_SEC" | "UP_TO_30_SEC";
const CONTENT_MODE_OPTIONS: ReadonlyArray<{ readonly value: ContentMode; readonly label: string }> = [
  { value: "SFW", label: "SFW" },
  { value: "SPICY", label: "SPICY" },
  { value: "NSFW", label: "NSFW" },
];
const VIDEO_LENGTH_OPTIONS: ReadonlyArray<{ readonly value: VideoLength; readonly label: string }> = [
  { value: "UP_TO_15_SEC", label: "Up to 15 sec" },
  { value: "UP_TO_30_SEC", label: "Up to 30 sec" },
];

const FIXED_NEGATIVE_PROMPT = "blur, ugly, robotic motion";

/**
 * Generic reference-backed modification group registry, rendered
 * entirely via `.map()` below — NOT a hardcoded "Pendant" selector.
 * Mirrors the backend's `REFERENCE_MODIFICATION_GROUPS` in
 * `packages/application/src/wavespeed/visual-modification-request.ts`
 * (each preset's `prompt` is byte-for-byte the same string as that
 * package's own registry entries) — duplicated here rather than
 * imported because no `@emma/application` -> `apps/dashboard`
 * cross-package import exists anywhere in this codebase yet (an
 * established convention, not an oversight; the same convention the
 * prior single-group `PENDANT_PRESETS` mirror already used).
 *
 * Adding a future group (e.g. Tattoo) means adding ONE more entry to
 * THIS array — the JSX below already loops over every registered group
 * and already knows how to build its submission payload generically
 * (see `handleConfirmGenerate`'s `referenceModifications` construction).
 * No new component, no new branch. `pendant` is the one group with a
 * dedicated backend wire field (back-compat with already-proven
 * behavior) — `handleConfirmGenerate` special-cases ONLY that one
 * field's name, nothing else about its rendering or state handling.
 * Each group's `none.prompt` is `null` — the UI's own signal for "no
 * selection in this group," never itself sent to the server (only a
 * `prompt: string` is ever placed on the wire, when a real preset is
 * selected).
 */
interface UiReferenceModificationPreset {
  readonly id: string;
  readonly label: string;
  readonly prompt: string | null;
}
interface UiReferenceModificationGroup {
  readonly id: string;
  readonly label: string;
  readonly presets: ReadonlyArray<UiReferenceModificationPreset>;
}
const REFERENCE_MODIFICATION_GROUPS: ReadonlyArray<UiReferenceModificationGroup> = [
  {
    id: "pendant",
    label: "Pendant / Accessory",
    presets: [
      { id: "none", label: "None", prompt: null },
      { id: "gold-cross", label: "Gold Cross", prompt: "A delicate golden cross necklace" },
      { id: "diamond-heart", label: "Diamond Heart", prompt: "A delicate silver necklace with a small heart-shaped diamond pendant, elegant and subtle" },
    ],
  },
  // Glasses (2026-09-11) — the second real registered group, added
  // exactly the way this registry's own doc comment above says a future
  // group should be: one more array entry, zero new components, zero new
  // branches in the selector JSX or in `handleConfirmGenerate`'s payload
  // construction (both already loop over this array generically). Not a
  // sequential-chain sub-stage — flows into the SAME single Direct Phota
  // submission `referenceModifications.glasses` already goes through,
  // exactly like clothing color and pendant. Defaults to "none" (see
  // `groupSelections`'s own init below) — an explicit, independently-set
  // choice, never bundled with Clothing Color's own checkbox.
  {
    id: "glasses",
    label: "Glasses",
    presets: [
      { id: "none", label: "None", prompt: null },
      { id: "classic-glasses", label: "Classic Glasses", prompt: "Classic black rectangular glasses with clear lenses" },
    ],
  },
];

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function CreateTab({ tenantId }: { readonly tenantId: string }) {
  const { csrfToken } = useAuth();
  // "Use a previous upload": NOT loaded on entering Create. The first page (newest-first) is fetched only when the user opens the picker; further pages only
  // on an explicit "Load more". `sourceVideosTotal` (server totalCount) is null until the first page has arrived.
  const [sourceVideos, setSourceVideos] = useState<SourceVideoSummary[]>([]);
  const [sourceVideosTotal, setSourceVideosTotal] = useState<number | null>(null);
  const [sourceVideosNextCursor, setSourceVideosNextCursor] = useState<string | null>(null);
  const [sourceVideosLoading, setSourceVideosLoading] = useState(false);
  const [sourceVideosError, setSourceVideosError] = useState<string | null>(null);
  const [personas, setPersonas] = useState<PersonaSummary[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // FIX-053: arriving from an approved Reel (REEL -> "Continue to video creation") the source video and the persona come in the URL
  // (`?sourceVideoId=...&personaId=...`). This is an ISOLATED pre-selection: the source video is read by its exact id (never searched for in the list / its pages),
  // and only PRE-SELECTED - nothing is submitted. Without those parameters nothing below runs and the screen behaves exactly as before.
  const [preselect] = useState<{ readonly sourceVideoId: string | null; readonly personaId: string | null }>(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const clean = (v: string | null): string | null => (v !== null && v.trim().length > 0 && v.length <= 200 ? v : null);
      return { sourceVideoId: clean(q.get("sourceVideoId")), personaId: clean(q.get("personaId")) };
    } catch {
      return { sourceVideoId: null, personaId: null };
    }
  });
  const [preselectedSource, setPreselectedSource] = useState<SourceVideoSummary | null>(null);
  const [preselectNotice, setPreselectNotice] = useState<string | null>(null);
  const [selectedSourceVideoId, setSelectedSourceVideoId] = useState<string | null>(null);
  // FIX-015 (Source Video reuse-picker truthfulness, 2026-09): which row's
  // Preview player is currently open — deliberately SEPARATE from
  // `selectedSourceVideoId`. Opening a preview must never select that
  // source video, and selecting one must never open its preview; a row
  // can be previewed without being chosen, and vice versa.
  const [previewingSourceVideoId, setPreviewingSourceVideoId] = useState<string | null>(null);
  // FIX-003 (Create cleanup): the historical source-video list is hidden
  // by default — only shown on demand while there is no current
  // selection, never once a source is chosen. See the "Source Video"
  // card below for the full current-source-only rule.
  const [showSourcePicker, setShowSourcePicker] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(null);
  // Canonical Persona Swap Gate 2 workflow (2026-09): both REQUIRED,
  // neither has a default — Generate stays disabled until the user
  // explicitly picks exactly one of each (see `missingRequirements`
  // below). The backend resolves the actual Gate 2 model from these two
  // values alone; this component never knows or sends a model id.
  const [contentMode, setContentMode] = useState<ContentMode | null>(null);
  const [videoLength, setVideoLength] = useState<VideoLength | null>(null);
  // Explicit None | Custom selector (2026-09-11 correction) — mirrors
  // Pendant/Glasses's own explicit-None `<select>` contract exactly.
  // Replaces a prior plain enable/disable checkbox, which represented
  // "no clothing change" only implicitly (unchecked) rather than as a
  // real, visible None state alongside the other two targets. Defaults
  // to "custom" — byte-identical to this form's prior default behavior
  // (previously `clothingEnabled = true` with target garment/color
  // "top"/"yellow") — this correction must not silently change what an
  // unattended submission requests.
  const [clothingSelection, setClothingSelection] = useState<"none" | "custom">("custom");
  const [clothingColor, setClothingColor] = useState("yellow");
  const [clothingGarment, setClothingGarment] = useState("top");
  // Generic per-group selection state, keyed by group id. Defaults to
  // "gold-cross" for the "pendant" group — byte-identical to this form's
  // prior default behavior (`pendantEnabled = true` with the one fixed
  // Gold Cross prompt) — the registry generalization must not silently
  // change what an unattended submission requests. Any future group
  // defaults to its own "none" preset, needing no new state field.
  const [groupSelections, setGroupSelections] = useState<Record<string, string>>(() =>
    Object.fromEntries(REFERENCE_MODIFICATION_GROUPS.map((group) => [group.id, group.id === "pendant" ? "gold-cross" : "none"])),
  );
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Product rule (2026-10-03): CREATE only STARTS a video; REVIEWS is the one place where every workflow in production is shown and tracked. This id only lets Create confirm, in one line with a link to
  // REVIEWS, that the start was accepted - it is never rendered as progress and is not kept across a reload (the artifact itself lives on the server and in REVIEWS).
  const [startedArtifactId, setStartedArtifactId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  /**
   * Duplicate-submission fix (2026-09-12, real incident): the Direct
   * Phota Profile submission is a single synchronous HTTP call that has
   * taken up to ~100 seconds in production, with nothing on screen but a
   * static "Starting…" label the whole time — indistinguishable from a
   * genuinely stuck request. That perceived "stuck" state is what led a
   * real user to click "Confirm & Start Gate 1" a second time, minting a
   * second `clientRequestId` and paying for a second, materially-
   * identical Gate 1 attempt (the backend's own recent-duplicate guard,
   * `persona-source-still-routes.ts`'s `handleSubmit`, now also catches
   * this server-side — this is the client-side half: give the user
   * enough feedback that a second click is never their only reasonable
   * next move). Ticks once per second only while `submitting` is true.
   */
  const [submittingElapsedSeconds, setSubmittingElapsedSeconds] = useState(0);
  /**
   * UI reconciliation fix (2026-09-14, production incident): a raw
   * network-level failure of the long Gate 1 POST below (e.g. "Failed to
   * fetch" — the browser lost the connection while the backend was still
   * working, or was still working when the response would have arrived)
   * is NOT proof the submission failed — the backend request is
   * synchronous and can legitimately run up to ~2 minutes; a real
   * artifact may already exist. `reconciling` is a distinct phase from
   * `submitting`'s initial POST: it means "the POST itself is no longer
   * in flight from this tab's point of view; we are now checking, via
   * the original clientRequestId, whether it actually landed" — the
   * Confirm button stays disabled and NO second POST is ever sent while
   * this is true, exactly like during the original submission.
   */
  const [reconciling, setReconciling] = useState(false);

  useEffect(() => {
    if (!submitting) {
      setSubmittingElapsedSeconds(0);
      return;
    }
    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      setSubmittingElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [submitting]);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      setLoadError(null);
      try {
        const personaResult = await api.get<{ personas: PersonaSummary[] }>(`/videos/${tenantId}/personas`);
        if (cancelled) return;
        setPersonas(personaResult.personas);
        // Section 7 fix: never leave the user staring at an unselected
        // Persona with no explanation. Exactly one usable Persona, or a
        // real backend-declared default, is auto-selected; with multiple
        // non-default Personas the user must still choose explicitly.
        // Persona onboarding UX hardening (2026-09-12): auto-select only
        // considers Generate-ready personas (photaReadinessState === "ready")
        // — a persona still collecting references or mid-training must
        // never end up invisibly selected, even as the sole/default one.
        const readyForAutoSelect = personaResult.personas.filter((p) => p.photaReadinessState === "ready");
        // Coming from an approved Reel: the persona the user chose there is selected when it is Generate-ready. When it is not, NO other persona is chosen on the
        // user's behalf (the default / single-persona auto-selection is skipped) and the user is told; the usual rules apply only when nothing came from a Reel.
        const wantedFromReel = preselect.personaId;
        const wantedIsReady = wantedFromReel !== null && readyForAutoSelect.some((p) => p.personaId === wantedFromReel);
        if (wantedFromReel !== null && !wantedIsReady) setPreselectNotice((n) => n ?? "The persona chosen for this Reel is not available for video creation right now. Please choose a persona.");
        setSelectedPersonaId((current) => {
          if (current !== null) return current;
          if (wantedFromReel !== null) return wantedIsReady ? wantedFromReel : current;
          if (readyForAutoSelect.length === 1) return readyForAutoSelect[0]!.personaId;
          return readyForAutoSelect.find((p) => p.isDefault === true)?.personaId ?? current;
        });
      } catch (error) {
        if (!cancelled) setLoadError(error instanceof ApiError ? error.message : "Failed to load CREATE options from Production.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, selectedPersonaId]);

  async function loadSourceVideosPage(cursor: string | null): Promise<void> {
    if (sourceVideosLoading) return;
    setSourceVideosLoading(true);
    setSourceVideosError(null);
    try {
      const query = cursor === null ? "" : `?cursor=${encodeURIComponent(cursor)}`;
      const page = await api.get<{ sourceVideos: SourceVideoSummary[]; totalCount: number; nextCursor: string | null }>(`/videos/${tenantId}/source-videos${query}`);
      setSourceVideos((previous) => {
        if (cursor === null) return page.sourceVideos; // first page replaces whatever an earlier upload had placed locally (the server list already contains it)
        const known = new Set(previous.map((sv) => sv.id));
        return [...previous, ...page.sourceVideos.filter((sv) => !known.has(sv.id))];
      });
      setSourceVideosTotal(page.totalCount);
      setSourceVideosNextCursor(page.nextCursor);
    } catch (error) {
      setSourceVideosError(error instanceof ApiError ? error.message : "Failed to load previous uploads.");
    } finally {
      setSourceVideosLoading(false);
    }
  }

  // FIX-053: the source video that came from an approved Reel is read BY ITS EXACT ID (read-only; tenant + ownership are checked by the server) and then selected -
  // it does not matter how many other uploads exist or where it would sit in the list. If it cannot be loaded (missing, removed, not permitted) nothing is selected
  // and the user is told; nothing is started either way.
  useEffect(() => {
    const id = preselect.sourceVideoId;
    if (id === null) return;
    let cancelled = false;
    void api
      .get<SourceVideoSummary>(`/videos/${tenantId}/source-videos/${encodeURIComponent(id)}`)
      .then((record) => {
        if (cancelled) return;
        setPreselectedSource(record);
        setSelectedSourceVideoId(record.id);
      })
      .catch(() => {
        if (!cancelled) setPreselectNotice("The saved source video for this Reel could not be loaded (it may have been removed, or it is not available to you). Nothing was started. Choose or upload a source video below.");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleSourcePicker(): void {
    const opening = !showSourcePicker;
    setShowSourcePicker(opening);
    if (opening && sourceVideosTotal === null) {
      void loadSourceVideosPage(null);
    }
  }

  async function handleUpload(): Promise<void> {
    if (uploadFile === null) return;
    setUploading(true);
    setSubmitError(null);
    try {
      const response = await fetch(`/videos/${tenantId}/source-videos?contentType=${encodeURIComponent(uploadFile.type || "video/mp4")}`, {
        method: "POST",
        credentials: "include",
        headers: { "x-emma-csrf": csrfToken ?? "" },
        body: uploadFile,
      });
      const body = (await response.json()) as SourceVideoSummary & { error?: string };
      if (!response.ok) {
        throw new Error(body.error ?? "upload failed");
      }
      const alreadyListed = sourceVideos.some((sv) => sv.id === body.id);
      setSourceVideos((previous) => (previous.some((sv) => sv.id === body.id) ? previous : [body, ...previous]));
      if (!alreadyListed) setSourceVideosTotal((total) => (total === null ? null : total + 1));
      setSelectedSourceVideoId(body.id);
      setShowSourcePicker(false);
      setUploadFile(null);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  /**
   * UI reconciliation fix (2026-09-14): bounded retry delays (ms) between
   * lookup attempts against `GET .../persona-source-stills/by-client-
   * request-id/:clientRequestId` — used only when a lookup attempt itself
   * came back temporarily indeterminate (a further network failure, or a
   * non-404 backend error on the lookup). 5 total attempts, ~19s of
   * waiting at most — bounded, never indefinite, and never a resubmit of
   * the original POST.
   */
  const RECONCILE_RETRY_DELAYS_MS = [2000, 3000, 5000, 8000];

  function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  /**
   * Looks up the artifact this tab's own submission would have created,
   * by the SAME clientRequestId already sent on the POST — never sends a
   * second POST. Called only after the original POST itself failed at the
   * network level (see `handleConfirmGenerate`'s catch below): a network
   * failure proves nothing about whether the backend actually received
   * and processed the request, since that call can legitimately run up to
   * ~2 minutes.
   */
  async function reconcileAfterNetworkFailure(clientRequestId: string): Promise<void> {
    setReconciling(true);
    for (let attempt = 0; attempt < 1 + RECONCILE_RETRY_DELAYS_MS.length; attempt += 1) {
      if (attempt > 0) await sleep(RECONCILE_RETRY_DELAYS_MS[attempt - 1]!);
      try {
        const artifact = await api.get<{ id: string }>(`/videos/${tenantId}/persona-source-stills/by-client-request-id/${encodeURIComponent(clientRequestId)}`);
        // Found: the submission DID land despite the network error this
        // tab observed. Reuse it exactly like a normal successful
        // submission — never resubmit.
        setStartedArtifactId(artifact.id);
        setShowConfirm(false);
        setSubmitError(null);
        setReconciling(false);
        setSubmitting(false);
        return;
      } catch (lookupError) {
        if (lookupError instanceof ApiError && lookupError.status === 404) {
          // Definitive: no artifact exists for this clientRequestId — the
          // original POST genuinely never reached/completed on the
          // backend. This is a real failure, safe to report as one.
          setSubmitError("Generation request failed to reach the server, and no attempt was recorded. Please try again.");
          setReconciling(false);
          setSubmitting(false);
          return;
        }
        // Any other outcome (another network failure, or a non-404
        // backend error on the LOOKUP itself) is still indeterminate —
        // keep retrying within the bounded policy above rather than
        // guessing either way.
      }
    }
    // Retries exhausted with no definitive answer either way — truthfully
    // report uncertainty rather than a fabricated success or failure.
    setSubmitError("Couldn't confirm whether your last request went through due to a network issue. Check History before trying again — do not resubmit blindly.");
    setReconciling(false);
    setSubmitting(false);
  }

  async function handleConfirmGenerate(): Promise<void> {
    if (selectedSourceVideoId === null || selectedPersonaId === null || contentMode === null || videoLength === null) return;
    setSubmitting(true);
    setSubmitError(null);
    // Hoisted above the try so the SAME clientRequestId is available to
    // the network-failure reconciliation path below — the whole point of
    // reconciliation is looking up this exact request, not a new one.
    const clientRequestId = `generate-ui-${crypto.randomUUID()}`;
    try {
      // Generic across every registered group: "pendant" keeps its own
      // dedicated wire field (back-compat with already-proven behavior);
      // any OTHER group's selection flows into the generic
      // `referenceModifications` map with zero new code here when a
      // future group is registered above.
      const referenceModifications: Record<string, { enabled: true; prompt: string }> = {};
      for (const group of REFERENCE_MODIFICATION_GROUPS) {
        if (group.id === "pendant") continue;
        const preset = selectedPresetForGroup(group.id);
        if (preset.prompt !== null) {
          referenceModifications[group.id] = { enabled: true, prompt: preset.prompt };
        }
      }
      const visualModifications = {
        ...(clothingSelection === "custom" ? { clothingColor: { enabled: true, targetColor: clothingColor, targetGarment: clothingGarment } } : {}),
        ...(selectedPendantPreset.prompt !== null ? { pendant: { enabled: true, prompt: selectedPendantPreset.prompt } } : {}),
        ...(Object.keys(referenceModifications).length > 0 ? { referenceModifications } : {}),
      };
      // Canonical Persona Swap Gate 2 workflow (2026-09): the only two
      // values this component ever sends toward Gate 2's model choice —
      // never a model id. The backend's own `resolveGate2Model` resolver
      // is the sole source of truth for which WaveSpeed model these two
      // choices produce.
      //
      // UI reconciliation fix (2026-09-14): routed through the shared
      // `api.post` client instead of a raw `fetch()` specifically so a
      // definitive backend error response (4xx/5xx — `ApiError`) can be
      // told apart from a raw network-level failure (connection dropped,
      // DNS/timeout, etc. — anything else `fetch()` itself throws) in the
      // catch block below. A raw fetch() could not make that distinction.
      const result = await api.post<{ id?: string; error?: string }>(
        `/videos/${tenantId}/persona-source-stills?personaId=${encodeURIComponent(selectedPersonaId)}&clientRequestId=${encodeURIComponent(clientRequestId)}&sourceVideoId=${encodeURIComponent(selectedSourceVideoId)}&visualModifications=${encodeURIComponent(JSON.stringify(visualModifications))}&contentMode=${encodeURIComponent(contentMode)}&videoLength=${encodeURIComponent(videoLength)}`,
        csrfToken,
      );
      setStartedArtifactId(result.id ?? null);
      setShowConfirm(false);
      setSubmitting(false);
    } catch (error) {
      if (error instanceof ApiError) {
        // A definitive response was received from the backend (it ran
        // the request and rejected it) — a genuine failure, safe to
        // report immediately exactly as before.
        setSubmitError(error.message);
        setSubmitting(false);
        return;
      }
      // No response was received at all — reconcile via clientRequestId
      // rather than assuming failure. reconcileAfterNetworkFailure clears
      // `submitting`/`reconciling` itself once it reaches a real answer.
      await reconcileAfterNetworkFailure(clientRequestId);
    }
  }

  if (loading) return <div className="empty-state">Loading…</div>;
  if (loadError) return <div className="error-banner">{loadError}</div>;

  // Section 7 fix: an explicit, itemized list of what's missing — never
  // a merely-gray button the user has to reverse-engineer.
  // Persona onboarding UX hardening (2026-09-12): ONE Generate-ready
  // collection, used consistently for rendering, missingRequirements,
  // selectedPersona, and canSubmit — a training/low-image persona can
  // never end up invisibly selected or leave Generate enabled.
  const readyPersonas = personas.filter((p) => p.photaReadinessState === "ready");
  const missingRequirements: string[] = [];
  if (selectedSourceVideoId === null) missingRequirements.push("Select a Source Video");
  if (readyPersonas.length === 0) missingRequirements.push("No Persona is ready yet — go to the PERSONAS tab to upload your model");
  else if (selectedPersonaId === null || !readyPersonas.some((p) => p.personaId === selectedPersonaId)) missingRequirements.push("Select a Persona");
  if (clothingSelection === "custom" && clothingColor.trim().length === 0) missingRequirements.push("Enter a target clothing color");
  if (contentMode === null) missingRequirements.push("Select a Content Mode");
  if (videoLength === null) missingRequirements.push("Select a Video Length");
  const canSubmit = missingRequirements.length === 0;
  const selectedSourceVideo = sourceVideos.find((sv) => sv.id === selectedSourceVideoId) ?? (preselectedSource !== null && preselectedSource.id === selectedSourceVideoId ? preselectedSource : null);
  const selectedPersona = readyPersonas.find((p) => p.personaId === selectedPersonaId) ?? null;
  function selectedPresetForGroup(groupId: string): UiReferenceModificationPreset {
    const group = REFERENCE_MODIFICATION_GROUPS.find((candidate) => candidate.id === groupId)!;
    const presetId = groupSelections[groupId] ?? "none";
    return group.presets.find((preset) => preset.id === presetId) ?? group.presets[0]!;
  }
  const selectedPendantPreset = selectedPresetForGroup("pendant");

  return (
    <div>
      {startedArtifactId !== null && (
        <div className="card" data-testid="gate1-started-notice" style={{ borderColor: "var(--accent)" }}>
          <strong>Gate 1 started.</strong> <span className="muted">Follow it in REVIEWS - this screen only starts new videos.</span> <Link to="/generate/reviews">Open REVIEWS →</Link>
        </div>
      )}
      {submitError && <div className="error-banner">{submitError}</div>}
      {preselectedSource !== null && selectedSourceVideoId === preselectedSource.id && (
        <div className="card" data-testid="from-reel-banner" style={{ borderColor: "var(--accent)" }}>
          <strong>
            {preselect.personaId !== null && selectedPersonaId === preselect.personaId ? "Source video and persona loaded from your approved Reel." : "Source video loaded from your approved Reel."}
          </strong>{" "}
          <span className="muted">Choose Content Mode and Video Length to continue — nothing has started.</span>
        </div>
      )}
      {preselectNotice !== null && (
        <div className="error-banner" role="alert" data-testid="preselect-notice">
          {preselectNotice}
        </div>
      )}

      <div className="grid grid-2">
        <div className="card">
          <h3>Source Video</h3>
          {/*
           * FIX-003 (Create cleanup): CREATE is for the current/new
           * creation only. With no current source chosen, the historical
           * upload list is NOT shown by default — only the upload
           * control, plus an on-demand "use a previous upload" toggle.
           * Once a source is selected, this collapses to a compact
           * "current source" summary — the full history stays reachable
           * in HISTORY, not duplicated here. No source video or upload
           * record is ever deleted by this — purely which one is shown.
           */}
          {selectedSourceVideo ? (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                {/* FIX-015 follow-up (2026-09, user-reported): the SAME thumbnail shown in the picker, now also shown for the CURRENT selection — the one point a visual confirmation matters most, right before submitting a paid generation. */}
                <img
                  src={`/videos/${tenantId}/source-videos/${selectedSourceVideo.id}/thumbnail`}
                  alt={`Selected source video uploaded ${new Date(selectedSourceVideo.createdAt).toLocaleString()}`}
                  style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 4, background: "var(--surface-2)" }}
                  data-testid={`source-video-thumbnail-${selectedSourceVideo.id}`}
                />
                <p className="muted" style={{ margin: 0 }}>
                  Current source: {new Date(selectedSourceVideo.createdAt).toLocaleString()} — {formatBytes(selectedSourceVideo.sizeBytes)}
                  {selectedSourceVideo.durationSeconds !== null ? ` — ${selectedSourceVideo.durationSeconds.toFixed(1)}s` : ""}
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedSourceVideoId(null);
                  setShowSourcePicker(false);
                }}
              >
                Change source video
              </button>
            </div>
          ) : (
            <div>
              <div className="field">
                <label htmlFor="upload">Upload New Source Video</label>
                <input id="upload" type="file" accept="video/mp4,video/quicktime" onChange={(event) => setUploadFile(event.target.files?.[0] ?? null)} />
              </div>
              <button disabled={uploadFile === null || uploading} onClick={() => void handleUpload()}>
                {uploading ? "Uploading…" : "Upload Source Video"}
              </button>
              <div style={{ marginTop: 12 }}>
                  <button onClick={toggleSourcePicker}>
                    {showSourcePicker ? "Hide previous uploads" : sourceVideosTotal === null ? "Use a previous upload" : `Use a previous upload (${sourceVideosTotal})`}
                  </button>
                  {showSourcePicker && sourceVideosError !== null && <div className="error-banner" style={{ marginTop: 8 }}>{sourceVideosError}</div>}
                  {showSourcePicker && sourceVideosTotal === 0 && <p className="muted">No previous uploads.</p>}
                  {showSourcePicker && sourceVideosLoading && sourceVideos.length === 0 && <p className="muted">Loading previous uploads…</p>}
                  {showSourcePicker && sourceVideos.length > 0 && (
                    <div style={{ maxHeight: 220, overflowY: "auto", marginTop: 8 }}>
                      <table>
                        <thead>
                          <tr>
                            <th></th>
                            <th>Thumbnail</th>
                            <th>Uploaded</th>
                            <th>Duration</th>
                            <th>Size</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {sourceVideos.map((sv) => (
                            <Fragment key={sv.id}>
                              <tr className="clickable" onClick={() => setSelectedSourceVideoId(sv.id)} style={{ background: selectedSourceVideoId === sv.id ? "var(--surface-2)" : undefined }}>
                                <td>{selectedSourceVideoId === sv.id ? "✓" : ""}</td>
                                <td>
                                  {/* FIX-015: a real, distinct thumbnail per source video — no generic icon, no fabricated placeholder. `handleThumbnail` 404s (rendered as a broken image, never a fake picture) if extraction genuinely can't produce one. */}
                                  <img
                                    src={`/videos/${tenantId}/source-videos/${sv.id}/thumbnail`}
                                    loading="lazy"
                                    alt={`Source video uploaded ${new Date(sv.createdAt).toLocaleString()}`}
                                    style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 4, background: "var(--surface-2)" }}
                                    data-testid={`source-video-thumbnail-${sv.id}`}
                                  />
                                </td>
                                <td>{new Date(sv.createdAt).toLocaleString()}</td>
                                <td>{sv.durationSeconds !== null ? `${sv.durationSeconds.toFixed(1)}s` : "—"}</td>
                                <td>{formatBytes(sv.sizeBytes)}</td>
                                <td>
                                  {/* FIX-015: a genuinely separate action from row-selection — stops propagation so clicking Preview never also selects this source video, and selecting a row never opens its preview. */}
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setPreviewingSourceVideoId((current) => (current === sv.id ? null : sv.id));
                                    }}
                                  >
                                    {previewingSourceVideoId === sv.id ? "Hide preview" : "Preview"}
                                  </button>
                                </td>
                              </tr>
                              {previewingSourceVideoId === sv.id && (
                                <tr>
                                  <td colSpan={6}>
                                    <video
                                      controls
                                      src={`/videos/${tenantId}/source-videos/${sv.id}/preview`}
                                      style={{ maxWidth: "100%", maxHeight: 240 }}
                                      data-testid={`source-video-preview-player-${sv.id}`}
                                    />
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          ))}
                        </tbody>
                      </table>
                      {sourceVideosNextCursor !== null && (
                        <button style={{ marginTop: 8 }} disabled={sourceVideosLoading} onClick={() => void loadSourceVideosPage(sourceVideosNextCursor)}>
                          {sourceVideosLoading ? "Loading…" : "Load more"}
                        </button>
                      )}
                    </div>
                  )}
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <h3>Persona</h3>
          {readyPersonas.length === 0 && <p className="muted">No personas configured for this workspace.</p>}
          <div className="grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            {readyPersonas.map((persona) => (
              <div
                key={persona.personaId}
                className="card"
                style={{ cursor: "pointer", borderColor: selectedPersonaId === persona.personaId ? "var(--accent)" : undefined, marginBottom: 0 }}
                onClick={() => setSelectedPersonaId(persona.personaId)}
              >
                <strong>{persona.displayName}</strong>
                {persona.isDefault && <span className="badge status-approved" style={{ marginLeft: 6 }}>Default</span>}
                <div className="muted">v{persona.identityVersion}</div>
              </div>
            ))}
          </div>

          <h3 style={{ marginTop: 20 }}>Content Mode</h3>
          <div style={{ display: "flex", gap: 8 }}>
            {CONTENT_MODE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={contentMode === option.value ? "primary" : undefined}
                aria-pressed={contentMode === option.value}
                onClick={() => setContentMode(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <h3 style={{ marginTop: 20 }}>Video Length</h3>
          <div style={{ display: "flex", gap: 8 }}>
            {VIDEO_LENGTH_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={videoLength === option.value ? "primary" : undefined}
                aria-pressed={videoLength === option.value}
                onClick={() => setVideoLength(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="card">
        <h3>Visual Modifications</h3>
        <div className="grid grid-2">
          <div>
            <div className="field">
              <label htmlFor="clothing-color-selection">Clothing Color</label>
              <select
                id="clothing-color-selection"
                value={clothingSelection}
                onChange={(event) => setClothingSelection(event.target.value === "none" ? "none" : "custom")}
              >
                <option value="none">None</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            {clothingSelection === "custom" && (
              <div className="grid grid-2" style={{ marginTop: 8 }}>
                <div className="field">
                  <label>Target Garment</label>
                  <input type="text" value={clothingGarment} onChange={(event) => setClothingGarment(event.target.value)} />
                </div>
                <div className="field">
                  <label>Target Color</label>
                  <input type="text" value={clothingColor} onChange={(event) => setClothingColor(event.target.value)} />
                </div>
              </div>
            )}
          </div>
          {/* Rendered FROM REFERENCE_MODIFICATION_GROUPS — a future group (e.g. Tattoo) means adding one registry entry above, never a new component or branch here. */}
          {REFERENCE_MODIFICATION_GROUPS.map((group) => {
            const selectedPreset = selectedPresetForGroup(group.id);
            return (
              <div key={group.id}>
                <div className="field">
                  <label htmlFor={`group-preset-${group.id}`}>{group.label}</label>
                  <select
                    id={`group-preset-${group.id}`}
                    value={groupSelections[group.id] ?? "none"}
                    onChange={(event) => setGroupSelections((previous) => ({ ...previous, [group.id]: event.target.value }))}
                  >
                    {group.presets.map((preset) => (
                      <option key={preset.id} value={preset.id}>
                        {preset.label}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedPreset.prompt !== null && <p className="muted" style={{ marginTop: 8 }}>{selectedPreset.prompt}</p>}
              </div>
            );
          })}
        </div>
        <button style={{ marginTop: 12 }} onClick={() => setShowAdvanced((v) => !v)}>
          {showAdvanced ? "Hide" : "Show"} Advanced Details
        </button>
        {showAdvanced && (
          <div className="kv" style={{ marginTop: 12 }}>
            <dt>Negative prompt (fixed)</dt>
            <dd>{FIXED_NEGATIVE_PROMPT}</dd>
          </div>
        )}
      </div>

      <div className="card">
        <button className="primary" disabled={!canSubmit} onClick={() => setShowConfirm(true)}>
          Generate
        </button>
        {!canSubmit && (
          <ul className="muted" style={{ marginTop: 10 }}>
            {missingRequirements.map((requirement) => (
              <li key={requirement}>{requirement}</li>
            ))}
          </ul>
        )}
      </div>

      {showConfirm && (
        <div className="card" style={{ borderColor: "var(--accent)" }}>
          <h3>Confirm Generation</h3>
          <div className="kv">
            <dt>Source Video</dt>
            <dd>{selectedSourceVideo ? `${new Date(selectedSourceVideo.createdAt).toLocaleString()} — ${formatBytes(selectedSourceVideo.sizeBytes)}` : "—"}</dd>
            <dt>Persona</dt>
            <dd>{selectedPersona?.displayName ?? "—"}</dd>
            <dt>Requested changes</dt>
            <dd>
              {clothingSelection === "custom" ? `${clothingGarment} → ${clothingColor}` : "no clothing change"}
              {REFERENCE_MODIFICATION_GROUPS.map((group) => {
                const preset = selectedPresetForGroup(group.id);
                return preset.prompt !== null ? `, + ${preset.label}` : "";
              }).join("")}
            </dd>
            <dt>Content Mode</dt>
            <dd>{contentMode ?? "—"}</dd>
            <dt>Video Length</dt>
            <dd>{videoLength === "UP_TO_15_SEC" ? "Up to 15 sec" : videoLength === "UP_TO_30_SEC" ? "Up to 30 sec" : "—"}</dd>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button className="primary" disabled={submitting} onClick={() => void handleConfirmGenerate()}>
              {reconciling ? "Confirming submission…" : submitting ? `Starting… (${submittingElapsedSeconds}s)` : "Confirm & Start Gate 1"}
            </button>
            <button onClick={() => setShowConfirm(false)}>Cancel</button>
          </div>
          {submitting && !reconciling && (
            <p className="muted" style={{ marginTop: 10 }}>
              This can take up to about 5 minutes — please wait and don't click Confirm again.
            </p>
          )}
          {/*
           * UI reconciliation fix (2026-09-14): shown only after the
           * original POST itself failed at the network level — distinct
           * copy from the normal in-flight message above, since at this
           * point the tab genuinely doesn't yet know whether the request
           * landed. Still never a second click / second POST.
           */}
          {reconciling && (
            <p className="muted" style={{ marginTop: 10 }}>
              We lost the connection while your request was processing — checking whether it went through. Please wait, we will not submit it again.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
